import { API_BASE_URL } from "../config";
import { FieldProfileResponse } from "../types";

const apiUrl = `${API_BASE_URL}/api`;

export interface UiFieldOption {
  value: string;
  label: string;
}

export type UiFieldCategories = Record<string, UiFieldOption[]>;

export interface NormalisedFieldProfile {
  categories: UiFieldCategories;
  defaultSelectedFields: string[];
  allowCustomFields: boolean;
  includeLegacyDefaultFields: boolean;
}

/**
 * Fetch the tenant/job-type field profile from backend.
 */
export async function fetchFieldProfile(
  accessToken: string,
  jobType: "MEMBER_EXTRACTION"
): Promise<FieldProfileResponse> {
  const url = `${apiUrl}/fields/profile?job_type=${encodeURIComponent(jobType)}`;
  const response = await fetch(url, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  });

  if (!response.ok) {
    const detail = await response.text().catch(() => "");
    throw new Error(
      detail
        ? `Failed to fetch field profile (${response.status}): ${detail}`
        : `Failed to fetch field profile (${response.status}).`
    );
  }

  return (await response.json()) as FieldProfileResponse;
}

/**
 * Convert backend profile payload into the existing UI category shape.
 */
export function normaliseFieldProfile(profile: FieldProfileResponse): NormalisedFieldProfile {
  const categories: UiFieldCategories = {};
  const selectedByDefaultFromFields: string[] = [];
  const orderedCategories = [...(profile.categories || [])].sort((a, b) => {
    return (a.order ?? 0) - (b.order ?? 0);
  });

  for (const category of orderedCategories) {
    const categoryLabel = (category.label || "").trim();
    if (!categoryLabel) {
      continue;
    }

    const orderedFields = [...(category.fields || [])].sort((a, b) => {
      return (a.order ?? 0) - (b.order ?? 0);
    });

    for (const field of orderedFields) {
      const fieldName = String(field.field_name || "").trim();
      if (!fieldName) {
        continue;
      }
      if (field.selected_by_default) {
        selectedByDefaultFromFields.push(fieldName);
      }
    }

    categories[categoryLabel] = orderedFields
      .map((field) => ({
        value: String(field.field_name || "").trim(),
        label: String(field.label || "").trim(),
      }))
      .filter((field) => field.value.length > 0 && field.label.length > 0);
  }

  return {
    categories,
    defaultSelectedFields:
      profile.default_selected_fields && profile.default_selected_fields.length > 0
        ? [...profile.default_selected_fields]
        : Array.from(new Set(selectedByDefaultFromFields)),
    allowCustomFields: Boolean(profile.allow_custom_fields),
    includeLegacyDefaultFields: Boolean(profile.include_legacy_default_fields),
  };
}
